import React, { FC, useCallback, useMemo } from 'react';
import clsx from 'clsx';
import { RoundedTriangleArrow, ExclamationInTriangle, Rectangle, ElipseWithBorder } from '@td-client-frontend/icons';
import { DefaultColumnType, HighlightedTruncatedText } from '@td-client-frontend/common';
import MarketingTable from './MarketingTable';
import { MarketingSearchType } from './MarketingSearch';
import { TO_MANY_DESCENDANTS } from '../../../hooks/useMarketingPreferences';
import { MarketingHierarchyData, MarketingHierarchyRowData } from '../../../@types/Marketing';
import { Nullable } from '../../../@types/common';
import { Row } from 'react-table';

interface GroupsHierarchyProps {
  data: MarketingHierarchyData[];
  searchTerm: string;
  searchType: MarketingSearchType;
  selectedEntity: Nullable<MarketingHierarchyRowData>;
  onEntitySelect: (x: MarketingHierarchyRowData) => void;
}

const GroupsHierarchy: FC<GroupsHierarchyProps> = ({
  data,
  searchTerm,
  searchType,
  selectedEntity,
  onEntitySelect
}) => {
  const calculateOpenedDescendants: (rows: Row<Record<string, any>>[]) => number = useCallback(
    (rows) =>
      rows.reduce(
        (acc, row) =>
          row.subRows.length > 0 && row.isExpanded ? acc + calculateOpenedDescendants(row.subRows) + 1 : acc + 1,
        0
      ),
    []
  );

  const handleClick = useCallback(
    (row: MarketingHierarchyData) => () => {
      const { subRows, ...rest } = row;
      onEntitySelect(rest);
    },
    [onEntitySelect]
  );

  const checkIfSubRowsHasToManyResults = (subRows: Row<Record<string, any>>[]) =>
    subRows?.some(({ id }) => id.includes(TO_MANY_DESCENDANTS));
  const columns: DefaultColumnType = useMemo(
    () => [
      {
        id: 'expender',
        Cell: ({ row }) => {
          const { subRows } = row;
          const subRowsLength = row?.subRows?.length || 0;
          const orgHasToManyResults = checkIfSubRowsHasToManyResults(row.original.subRows);

          // we don't need to calculate descendants in the last subRow
          const modifiedSubRows = subRowsLength > 1 ? subRows.slice(0, -1) : subRows;
          const descendantsLength =
            subRowsLength === 1 ? 1 : calculateOpenedDescendants(modifiedSubRows) + +!orgHasToManyResults;

          return (
            <div className="relative" {...row.getToggleRowExpandedProps()}>
              {row.depth !== 0 && (
                <div className="absolute border-b-1 w-4 sm:w-2 h-7 border-l-1 rounded-bl-lg top-1/2 transform -translate-y-7 -translate-x-5.5 md:-translate-x-5.5 sm:-translate-x-3" />
              )}
              {row.canExpand ? (
                <div className="w-full relative">
                  {row.isExpanded && (
                    <div
                      style={{ height: `${47 * descendantsLength - 25}px` }}
                      className="absolute border-l-1 top-6 transform translate-x-2"
                    />
                  )}
                  <RoundedTriangleArrow
                    data-testid="triangle"
                    aria-hidden="true"
                    className={clsx('transition duration-200 transform', !row.isExpanded && '-rotate-90')}
                  />
                </div>
              ) : (
                <ElipseWithBorder data-testid="circle" />
              )}
            </div>
          );
        }
      },
      {
        id: 'entity-name',
        accessor: 'name',
        Cell: (row) => {
          const { original: rowData } = row.cell.row;
          // TODO find another way to check if it's a group
          const isGroup = !row.cell.row.canExpand;
          const onlyGroupSearch = searchType === MarketingSearchType.GROUP && isGroup;
          const shouldHighlight =
            searchTerm && searchTerm.length >= 3 && (onlyGroupSearch || searchType === MarketingSearchType.ALL);
          // hiddenSubRows means that subRows don't match the search criteria and shouldn't be accounted
          const searchedDescendantsLength = rowData?.hiddenSubRows ? 0 : rowData?.subRows?.length;
          const allDescendantsLength = rowData.subRows?.[0]?.siblings?.length + 1 || 1;

          return (
            <button
              className="w-full flex relative items-center cursor-pointer py-2 border-0 border-b border-solid text-gray-700"
              onClick={handleClick(rowData as MarketingHierarchyData)}
            >
              <HighlightedTruncatedText searchTerm={shouldHighlight ? searchTerm : ''} textContent={row.value} />
              {!isGroup && (
                <span className="mx-1 text-gray-700 whitespace-nowrap text-lg leading-7">{`(${searchedDescendantsLength} of ${allDescendantsLength})`}</span>
              )}
              <div className="ml-auto flex">
                {rowData?.marketingExclusion?.enabledAt && (
                  <ExclamationInTriangle aria-hidden="true" data-testid="flagged-group" className="ml-auto mr-1" />
                )}
                {rowData?.marketingProfile && (
                  <Rectangle data-testid="custom-group" aria-hidden="true" className="ml-auto mr-1" />
                )}
              </div>
            </button>
          );
        }
      }
    ],
    [calculateOpenedDescendants, handleClick, searchTerm, searchType]
  );

  return <MarketingTable columns={columns} data={data} selectedEntity={selectedEntity} />;
};

export default GroupsHierarchy;
